import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { blogAPI } from '../services/api';
import './BlogPostDetail.css';

interface Comment {
  id: number;
  author_name: string;
  content: string;
  created_at: string;
}

interface BlogPost {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  category_name: string;
  author_name: string;
  image_url: string;
  read_time: number;
  published_at: string;
  comments: Comment[];
}

const BlogPostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentData, setCommentData] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await blogAPI.getPostBySlug(slug!);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Blog post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCommentData({
      ...commentData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentData.author_name || !commentData.author_email || !commentData.content) {
      alert('Please fill all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await blogAPI.submitComment({
        post: post!.id,
        ...commentData
      });
      setCommentSubmitted(true);
      setCommentData({ author_name: '', author_email: '', content: '' });
      setTimeout(() => setCommentSubmitted(false), 5000);
      fetchPost();
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // SVG Icons
  const Icons = {
    Back: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
    ),
    User: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    Calendar: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    Clock: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    Facebook: () => (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    ),
    Twitter: () => (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
      </svg>
    ),
    LinkedIn: () => (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
        <rect x="2" y="9" width="4" height="12"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    ),
    Spinner: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      </svg>
    ),
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="blog-post-loading">
          <div className="blog-post-spinner">
            <Icons.Spinner />
          </div>
          <p>Loading article...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Navbar />
        <div className="blog-post-error">
          <div className="blog-post-error-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <circle cx="12" cy="16" r="0.5" fill="#dc3545"/>
            </svg>
          </div>
          <h2>Article Not Found</h2>
          <p>The blog post you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/blog')}>Back to Blog</button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="blog-post-page">
        <div className="blog-post-container">
          <button className="blog-post-back" onClick={() => navigate('/blog')}>
            <Icons.Back />
            Back to Blog
          </button>

          {/* Hero Section */}
          <div className="blog-post-hero">
            <div className="blog-post-category-wrapper">
              <span className="blog-post-category">{post.category_name}</span>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            <div className="blog-post-meta">
              <span className="blog-post-meta-item">
                <Icons.User />
                {post.author_name}
              </span>
              <span className="blog-post-meta-item">
                <Icons.Calendar />
                {formatDate(post.published_at)}
              </span>
              <span className="blog-post-meta-item">
                <Icons.Clock />
                {post.read_time} min read
              </span>
            </div>
          </div>

          {/* Featured Image */}
          {post.image_url && (
            <div className="blog-post-featured-image">
              <img src={post.image_url} alt={post.title} />
            </div>
          )}

          {/* Content */}
          <div className="blog-post-content-wrapper">
            <div 
              className="blog-post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Share Section */}
          <div className="blog-post-share-wrapper">
            <div className="blog-post-share">
              <span className="blog-post-share-label">Share this article:</span>
              <div className="blog-post-share-buttons">
                <button className="blog-post-share-btn facebook">
                  <Icons.Facebook />
                  Facebook
                </button>
                <button className="blog-post-share-btn twitter">
                  <Icons.Twitter />
                  Twitter
                </button>
                <button className="blog-post-share-btn linkedin">
                  <Icons.LinkedIn />
                  LinkedIn
                </button>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="blog-post-comments">
            <h3>Comments ({post.comments?.length || 0})</h3>
            
            {commentSubmitted && (
              <div className="blog-post-comment-success">
                ✓ Your comment has been submitted and will appear after approval.
              </div>
            )}

            <form onSubmit={handleSubmitComment} className="blog-post-comment-form">
              <div className="blog-post-form-row">
                <div className="blog-post-form-group">
                  <input
                    type="text"
                    name="author_name"
                    className="blog-post-form-input"
                    placeholder="Your Name *"
                    value={commentData.author_name}
                    onChange={handleCommentChange}
                    required
                  />
                </div>
                <div className="blog-post-form-group">
                  <input
                    type="email"
                    name="author_email"
                    className="blog-post-form-input"
                    placeholder="Your Email *"
                    value={commentData.author_email}
                    onChange={handleCommentChange}
                    required
                  />
                </div>
              </div>
              <div className="blog-post-form-group">
                <textarea
                  name="content"
                  className="blog-post-form-textarea"
                  placeholder="Your Comment *"
                  rows={5}
                  value={commentData.content}
                  onChange={handleCommentChange}
                  required
                />
              </div>
              <button type="submit" className="blog-post-submit-comment" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Post Comment'}
              </button>
            </form>

            <div className="blog-post-comments-list">
              {post.comments && post.comments.length > 0 ? (
                post.comments.map(comment => (
                  <div key={comment.id} className="blog-post-comment-item">
                    <div className="blog-post-comment-header">
                      <span className="blog-post-comment-author">{comment.author_name}</span>
                      <span className="blog-post-comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <div className="blog-post-comment-content">{comment.content}</div>
                  </div>
                ))
              ) : (
                <p className="blog-post-no-comments">No comments yet. Be the first to comment!</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogPostDetail;